import { Config } from '@backstage/config';

export type AiChatWidgetConfig = {
  agent?: string;
  product?: string;
  title: string;
  endpoint: string;
  prompt?: string;
  model?: string;
  variant?: string;
  welcome?: string;
  examples?: string[];
  ratingEnabled?: boolean;
  logoUrl?: string;
  position?: string;
  sidebar?: string;
  autoHide?: boolean;
  additionalContext?: string;
};

export type AiChatWidgetRoute = {
  path: string;
  config: AiChatWidgetConfig;
};

function readAiChatWidgetConfig(config: Config): AiChatWidgetConfig {
  return {
    agent: config.getOptionalString('agent'),
    product: config.getOptionalString('product'),
    title: config.getString('title'),
    endpoint: config.getString('endpoint'),
    prompt: config.getOptionalString('prompt'),
    model: config.getOptionalString('model'),
    variant: config.getOptionalString('variant'),
    welcome: config.getOptionalString('welcome'),
    examples: config.getOptionalStringArray('examples'),
    ratingEnabled: config.getOptionalBoolean('ratingEnabled'),
    logoUrl: config.getOptionalString('logoUrl'),
    position: config.getOptionalString('position'),
    sidebar: config.getOptionalString('sidebar'),
    autoHide: config.getOptionalBoolean('autoHide'),
    additionalContext: config.getOptionalString('additionalContext'),
  };
}

// Controls whether the widget is rendered at all; read from app.aiChatWidget.enabled (defaults to true).
export function isAiChatWidgetEnabled(config: Config): boolean {
  return config.getOptionalBoolean('app.aiChatWidget.enabled') ?? true;
}

// URL of the widget bootstrap script; read from app.aiChatWidget.scriptUrl.
export function getAiChatWidgetScriptUrl(config: Config): string | undefined {
  return config.getOptionalString('app.aiChatWidget.scriptUrl');
}

// Used on any page that doesn't match a route below; read from app.aiChatWidget.defaultConfig.
export function getDefaultAiChatWidgetConfig(
  config: Config,
): AiChatWidgetConfig {
  return readAiChatWidgetConfig(
    config.getConfig('app.aiChatWidget.defaultConfig'),
  );
}

// First matching path prefix wins; read from app.aiChatWidget.routes.
export function getAiChatWidgetRoutes(config: Config): AiChatWidgetRoute[] {
  const routes = config.getOptionalConfigArray('app.aiChatWidget.routes') ?? [];
  return routes.map(route => ({
    path: route.getString('path'),
    config: readAiChatWidgetConfig(route.getConfig('config')),
  }));
}

// Path prefixes on which the widget is not rendered at all; read from app.aiChatWidget.disabledRoutes.
export function getAiChatWidgetDisabledRoutes(config: Config): string[] {
  return config.getOptionalStringArray('app.aiChatWidget.disabledRoutes') ?? [];
}
